import { useQuery } from "react-query";
import axios, { AxiosResponse } from "axios";
import { UserPermissionsAPI } from "../constants/API";
import { commonHeaders } from "../constants/HeadersAPI";
import { adminRoutesURLs, buildAdminPath } from "../constants/routes";
import { getUserData } from "../utils/Authorization/authorization";

export type PermissionDto = {
  permissionKey: string;
  canView: boolean;
  canEdit: boolean;
};

/** Маппинг: путь админки (сегмент или base/subpath) -> permissionKey для проверки canView */
export const adminPathToPermissionKey: Record<string, string> = {
  [adminRoutesURLs.employers]: "EMPLOYERS",
  [adminRoutesURLs.sectionServices]: "SECTION_SERVICES",
  [adminRoutesURLs.workplaces]: "WORKPLACES",
  [adminRoutesURLs.advertising]: "ADVERTISING",
  [adminRoutesURLs.userArea]: "USER_AREA",
  [adminRoutesURLs.terminalAppearance]: "SETTINGS_TERMINAL",
  [adminRoutesURLs.tabloSettings]: "SETTINGS_TABLO",
  [adminRoutesURLs.ratingSettings]: "SETTINGS_RATING",
  [adminRoutesURLs.branches]: "", // по роли
  [adminRoutesURLs.analyticsAndReports.analyticsAndReportsBase]:
    "ANALYTICS_GROUP",
  [adminRoutesURLs.analyticsAndReports.currentServicesStatus]:
    "ANALYTICS_CURRENT_SERVICES_STATUS",
  [adminRoutesURLs.analyticsAndReports.services]: "ANALYTICS_SERVICES",
  [adminRoutesURLs.analyticsAndReports.employersReports]:
    "ANALYTICS_EMPLOYERS_REPORTS",
  [adminRoutesURLs.analyticsAndReports.visitors]: "ANALYTICS_VISITORS",
  [adminRoutesURLs.analyticsAndReports.qualityReport]: "ANALYTICS_QUALITY_RATING",
};

const adminBase = "admin";

/**
 * По pathname (например /admin/employers или /admin/analytics-and-reports/services)
 * возвращает permissionKey для проверки canView. '' — доступ по роли (филиалы), null — не админ-путь.
 */
export function getPermissionKeyForPathname(pathname: string): string | null {
  const normalized = pathname.replace(/^\/+|\/+$/g, "");
  if (!normalized.startsWith(adminBase)) return null;
  const afterAdmin =
    normalized.slice(adminBase.length).replace(/^\/+/, "") || "";
  if (!afterAdmin) return null;

  const segments = afterAdmin.split("/");
  const first = segments[0];
  const second = segments[1];

  if (first === adminRoutesURLs.branches) return "";
  if (first === adminRoutesURLs.sectionServices) return "SECTION_SERVICES";
  if (first === adminRoutesURLs.workplaces) return "WORKPLACES";
  if (first === adminRoutesURLs.employers) return "EMPLOYERS";
  if (first === adminRoutesURLs.advertising) return "ADVERTISING";
  if (first === adminRoutesURLs.userArea) return "USER_AREA";
  if (first === adminRoutesURLs.terminalAppearance) return "SETTINGS_TERMINAL";
  if (first === adminRoutesURLs.tabloSettings) return "SETTINGS_TABLO";
  if (first === adminRoutesURLs.ratingSettings) return "SETTINGS_RATING";

  if (first === adminRoutesURLs.analyticsAndReports.analyticsAndReportsBase) {
    if (second === adminRoutesURLs.analyticsAndReports.currentServicesStatus)
      return "ANALYTICS_CURRENT_SERVICES_STATUS";
    if (second === adminRoutesURLs.analyticsAndReports.services)
      return "ANALYTICS_SERVICES";
    if (second === adminRoutesURLs.analyticsAndReports.employersReports)
      return "ANALYTICS_EMPLOYERS_REPORTS";
    if (second === adminRoutesURLs.analyticsAndReports.visitors)
      return "ANALYTICS_VISITORS";
    if (second === adminRoutesURLs.analyticsAndReports.qualityReport)
      return "ANALYTICS_QUALITY_RATING";
    return "ANALYTICS_GROUP";
  }

  return null;
}

/** Порядок путей для выбора "первого разрешённого" при редиректе (совпадает с сайдбаром) */
export const adminPathsOrder: { path: string; permissionKey: string }[] = [
  { path: adminRoutesURLs.branches, permissionKey: "" },
  { path: adminRoutesURLs.sectionServices, permissionKey: "SECTION_SERVICES" },
  { path: adminRoutesURLs.workplaces, permissionKey: "WORKPLACES" },
  { path: adminRoutesURLs.employers, permissionKey: "EMPLOYERS" },
  {
    path: `${adminRoutesURLs.analyticsAndReports.analyticsAndReportsBase}/${adminRoutesURLs.analyticsAndReports.currentServicesStatus}`,
    permissionKey: "ANALYTICS_GROUP",
  },
  { path: adminRoutesURLs.advertising, permissionKey: "ADVERTISING" },
  {
    path: adminRoutesURLs.terminalAppearance,
    permissionKey: "SETTINGS_TERMINAL",
  },
  {
    path: adminRoutesURLs.ratingSettings,
    permissionKey: "SETTINGS_RATING",
  },
  { path: adminRoutesURLs.userArea, permissionKey: "USER_AREA" },
];

export function useMyPermissions() {
  const { data: permissionsResp, isLoading } = useQuery(
    "myPermissions",
    () =>
      axios.get(
        UserPermissionsAPI.getMyPermissions,
        commonHeaders(),
      ) as Promise<AxiosResponse<PermissionDto[]>>,
    { enabled: localStorage.getItem("user") !== null },
  );

  const permissions = permissionsResp?.data ?? [];
  const userRoleRaw = (getUserData() as { role?: string })?.role ?? "";
  const userRole = userRoleRaw.trim();
  const isSuperAdmin = userRole === "Суперадминистратор Tehnosky";
  const isAdmin = userRole.startsWith("Администратор");

  /**
   * В проекте исторически используются два формата ключей:
   * - "групповые" (например SETTINGS_TERMINAL / SETTINGS_TABLO) где ожидается canView/canEdit
   * - "атомарные" *_VIEW/*_EDIT (например SETTINGS_VIEW/SETTINGS_EDIT), которые иногда приходят/сохраняются как canView=true, canEdit=false
   *
   * Чтобы не ломать существующий UI (матрицу прав) и проверки на страницах,
   * делаем совместимую проверку через алиасы и семантику *_EDIT.
   */
  const PERMISSION_ALIASES: Record<string, string[]> = {
    SETTINGS_TERMINAL: ["SETTINGS_TERMINAL", "SETTINGS_EDIT", "SETTINGS_VIEW", "SETTINGS_GROUP"],
    SETTINGS_TABLO: ["SETTINGS_TABLO", "SETTINGS_EDIT", "SETTINGS_VIEW", "SETTINGS_GROUP"],
    SETTINGS_RATING: ["SETTINGS_RATING", "SETTINGS_EDIT", "SETTINGS_VIEW", "SETTINGS_GROUP"],
    SETTINGS_GROUP: ["SETTINGS_GROUP", "SETTINGS_EDIT", "SETTINGS_VIEW"],

    // Раздел "Аналитика и отчеты" в матрице управляется ключом ANALYTICS_VIEW,
    // а страницы используют более "детальные" ключи ANALYTICS_*.
    // Поэтому любой ANALYTICS_* путь должен считаться разрешённым, если есть ANALYTICS_VIEW.
    ANALYTICS_GROUP: ["ANALYTICS_GROUP", "ANALYTICS_VIEW"],
    ANALYTICS_CURRENT_SERVICES_STATUS: [
      "ANALYTICS_CURRENT_SERVICES_STATUS",
      "ANALYTICS_VIEW",
    ],
    ANALYTICS_SERVICES: ["ANALYTICS_SERVICES", "ANALYTICS_VIEW"],
    ANALYTICS_EMPLOYERS_REPORTS: [
      "ANALYTICS_EMPLOYERS_REPORTS",
      "ANALYTICS_VIEW",
    ],
    ANALYTICS_VISITORS: ["ANALYTICS_VISITORS", "ANALYTICS_VIEW"],
    ANALYTICS_QUALITY_RATING: ["ANALYTICS_QUALITY_RATING", "ANALYTICS_VIEW"],
  };

  const getPermission = (permissionKey: string): PermissionDto | undefined =>
    permissions.find((p) => p.permissionKey === permissionKey);

  const isAtomicActionKey = (permissionKey: string) =>
    permissionKey.endsWith("_CREATE") ||
    permissionKey.endsWith("_EDIT") ||
    permissionKey.endsWith("_DELETE") ||
    permissionKey.endsWith("_ACTIVITY");

  const hasViewByKey = (permissionKey: string): boolean => {
    const p = getPermission(permissionKey);
    // "Просмотр" в матрице должен определять только canView=true.
    // canEdit не должен давать право на просмотр.
    return !!p?.canView;
  };

  const hasEditByKey = (permissionKey: string): boolean => {
    const p = getPermission(permissionKey);
    // В матрице прав "атомарные" ключи (*_CREATE/*_EDIT/*_DELETE/*_ACTIVITY) храним как canView=true, canEdit=false.
    // Поэтому считаем их доступными, если выставлен хотя бы один из флагов.
    if (isAtomicActionKey(permissionKey)) return !!(p?.canEdit || p?.canView);
    return !!p?.canEdit;
  };

  const resolveKeys = (permissionKey: string): string[] =>
    PERMISSION_ALIASES[permissionKey] ?? [permissionKey];

  const canView = (permissionKey: string): boolean => {
    if (isSuperAdmin) return true;
    if (!permissionKey) return true;
    return resolveKeys(permissionKey).some(hasViewByKey);
  };

  const canEdit = (permissionKey: string): boolean => {
    if (isSuperAdmin) return true;
    if (!permissionKey) return true;
    return resolveKeys(permissionKey).some(hasEditByKey);
  };

  const getFirstAllowedPath = (): string => {
    const userRole = ((getUserData() as { role?: string })?.role ?? "").trim();
    const isSuperAdmin = userRole === "Суперадминистратор Tehnosky";
    const isBranchAdmin = userRole.startsWith("Администратор");

    for (const { path, permissionKey } of adminPathsOrder) {
      if (path === adminRoutesURLs.branches) {
        if (isSuperAdmin || isBranchAdmin) return buildAdminPath(path);
        continue;
      }
      if (canView(permissionKey)) return buildAdminPath(path);
    }
    return buildAdminPath(adminRoutesURLs.sectionServices);
  };

  return { permissions, canView, canEdit, isLoading, getFirstAllowedPath };
}
